import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError, delay, mergeMap, tap } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { AuthenticationService } from "./authentication.service";
import { LoggerService } from "./logger.service";
import { Router } from "@angular/router";

const SECOND: number = 1000;
@Injectable({
  providedIn: "root",
})
export class VcloudHttpClientService {
  private http: HttpClient = inject(HttpClient);
  private auth: AuthenticationService = inject(AuthenticationService);
  private log: LoggerService = inject(LoggerService);
  private router: Router = inject(Router);
  public readonly baseUrl: string = environment.urlVcloudApi;

  public getHeaders(): HttpHeaders {
    const headers: { [key: string]: string } = {
      Authorization: `Bearer ${this.auth.getIdToken()}`,
      Accept: "application/json, */*",
    };
    const tenant: string = this.auth.tenant;
    if (tenant && tenant !== "undefined" && tenant !== "null") {
      headers["x-vf-api-tenant"] = tenant;
    }
    return new HttpHeaders(headers);
  }

  public request<T>(
    method: string,
    url: string,
    options: Partial<{
      headers: HttpHeaders;
      body: any;
      params: HttpParams;
    }> = {},
  ): Observable<T> {
    let headers: HttpHeaders = new HttpHeaders();

    try {
      headers = this.getHeaders();
    } catch (e) {
      if ((e as any).message === "Session Expired") {
        this.auth.autoLogout();
      }
      return throwError(e);
    }

    if (options.headers) {
      for (const key of options.headers.keys()) {
        headers = headers.set(key, options.headers.get(key) as string | string[]);
      }
    }

    const base: number = 1;
    const cap: number = 6;
    const sec: number = SECOND;
    let tries: number = 0;
    const maxTries: number = environment.production ? 4 : 0;

    if (this.auth.tenant === "vodacom_za" && this.router.url.includes("gcp")) {
      headers = headers.set("x-vf-api-tenant", "vodafone");
    }

    const request: Observable<T> = this.http.request<T>(method, `${this.baseUrl}/${url}`, {
      body: options.body,
      headers,
      observe: "body",
      responseType: "json",
      params: options.params,
    });

    const observable: Observable<T> = request.pipe(
      catchError((e) => {
        if (tries >= maxTries) {
          this.log.error(`Error while requesting ${method} ${url} (tried ${tries}/${maxTries} times)`, options, e);
          return throwError(e);
        }
        return of(null).pipe(
          tap(() => tries++),
          delay(Math.ceil(sec * (Math.random() * Math.min(cap, base * 2 ** tries)))),
          mergeMap(() => observable),
        );
      }),
    );
    return observable;
  }

  public get<T>(url: string): Observable<T> {
    return this.request<T>("get", url);
  }

  public post<T, K>(url: string, body: K): Observable<T> {
    return this.request<T>("post", url, { body });
  }

  public put<T, K>(url: string, body: K): Observable<T> {
    return this.request<T>("put", url, { body });
  }

  public patch<T, K>(url: string, body: K): Observable<T> {
    return this.request<T>("patch", url, { body });
  }

  public delete<T>(url: string): Observable<T> {
    return this.request<T>("delete", url);
  }
}
